import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Type, Eraser, ArrowLeft, FileText, Zap, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const CaseConverterPage = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);

  React.useEffect(() => {
    const bookmarkDismissed = localStorage.getItem('case-converter-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }
  }, []);

  // Conversion functions
  const toSentenceCase = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const toLowerCase = (text) => {
    return text.toLowerCase();
  };

  const toUpperCase = (text) => {
    return text.toUpperCase();
  };

  const toCapitalizedCase = (text) => {
    if (!text) return '';
    return text.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const toAlternatingCase = (text) => {
    if (!text) return '';
    return text.split('').map((char, index) => 
      index % 2 === 0 ? char.toLowerCase() : char.toUpperCase()
    ).join('');
  };

  const toTitleCase = (text) => {
    if (!text) return '';
    const smallWords = ['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with'];
    
    return text.toLowerCase().split(' ').map((word, index) => {
      // Always capitalize first word
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Don't capitalize small words unless they're the first word
      if (smallWords.includes(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  };

  const toInverseCase = (text) => {
    if (!text) return '';
    return text.split('').map(char => {
      if (char === char.toUpperCase()) {
        return char.toLowerCase();
      } else {
        return char.toUpperCase();
      }
    }).join('');
  };

  const handleConvert = (conversionType) => {
    let result = '';
    
    switch(conversionType) {
      case 'sentence':
        result = toSentenceCase(inputText);
        break;
      case 'lower':
        result = toLowerCase(inputText);
        break;
      case 'upper':
        result = toUpperCase(inputText);
        break;
      case 'capitalized':
        result = toCapitalizedCase(inputText);
        break;
      case 'alternating':
        result = toAlternatingCase(inputText);
        break;
      case 'title':
        result = toTitleCase(inputText);
        break;
      case 'inverse':
        result = toInverseCase(inputText);
        break;
      default:
        result = inputText;
    }
    
    setOutputText(result);
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('case-converter-bookmark-dismissed', 'true');
  };

  return (
    <>
      <Helmet>
        <title>Text Case Converter — Uppercase, Lowercase, Title Case | AllSmallTooLS</title>
        <meta name="description" content="Convert text to uppercase, lowercase, title case, or sentence case instantly. Free online text case converter." />
        <meta name="keywords" content="case converter, text to uppercase, lowercase tool, capitalize text, change case online" />
        <link rel="canonical" href="https://allsmalltools.com/text-case-converter" />
      </Helmet>

      <div className="space-y-6">
      {/* Bookmark Banner */}
      {showBookmarkBanner && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3 text-center sm:text-left">
              <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-gray-900">
                  Bookmark this page for quick access to case converter anytime
                </p>
                <p className="text-sm text-gray-600 hidden sm:block">
                  Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                  const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                  if (window.sidebar && window.sidebar.addPanel) {
                    window.sidebar.addPanel(document.title, window.location.href, '');
                  } else if (window.external && ('AddFavorite' in window.external)) {
                    window.external.AddFavorite(window.location.href, document.title);
                  } else {
                    alert(`To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your case converter for future use.`);
                  }
                }}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span>Bookmark Now</span>
              </button>

              <button
                onClick={handleDismissBookmarkBanner}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                aria-label="Dismiss bookmark banner"
                title="Don't show again"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
            <Type className="h-8 w-8 text-purple-500" />
            <span>Case Converter</span>
          </h1>
          <p className="text-gray-600">Convert text to different letter cases instantly</p>
        </div>
      </div>

      {/* Main Converter Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Input Box */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Input Text</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Enter your text here..."
              className="w-full flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-gray-900"
              style={{ minHeight: '400px' }}
            />
          </CardContent>
        </Card>

        {/* Conversion Buttons */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Convert To</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-center">
            <div className="flex flex-col space-y-2">
              <Button
                onClick={() => handleConvert('sentence')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Sentence case
              </Button>
              
              <Button
                onClick={() => handleConvert('lower')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                lower case
              </Button>
              
              <Button
                onClick={() => handleConvert('upper')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                UPPER CASE
              </Button>
              
              <Button
                onClick={() => handleConvert('capitalized')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Capitalized Case
              </Button>
              
              <Button
                onClick={() => handleConvert('alternating')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                aLtErNaTiNg cAsE
              </Button>
              
              <Button
                onClick={() => handleConvert('title')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Title Case
              </Button>
              
              <Button
                onClick={() => handleConvert('inverse')}
                className="w-full justify-center bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                InVeRsE CaSe
              </Button>
              
              <Button
                onClick={handleClear}
                variant="outline"
                className="w-full justify-center border-2 border-gray-400 text-gray-700 hover:bg-gray-100"
              >
                <Eraser className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Output Box */}
        <Card className="lg:col-span-1 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Output Text</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <textarea
              value={outputText}
              readOnly
              placeholder="Converted text will appear here..."
              className="w-full flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 resize-none"
              style={{ minHeight: '400px' }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <Type className="h-6 w-6 text-purple-600" />
            <div className="text-sm text-purple-700">
              <strong>How to use:</strong> Enter your text in the left box, click any conversion button in the middle, and see the result in the right box.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Why Use Our Case Converter Section */}
      <Card className="mt-12">
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Why Use Our Text Case Converter?</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">8 Case Styles</h3>
              <p className="text-gray-600">
                Convert text to UPPERCASE, lowercase, Title Case, Sentence case, Capitalized Case, aLtErNaTiNg cAsE, InVeRsE CaSe. Our case converter handles all text transformations instantly.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <Zap className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Instant Conversion</h3>
              <p className="text-gray-600">
                Change text case instantly with one click. No waiting, no processing time. Our text case converter transforms your text immediately for all case styles.
              </p>
            </div>
            
            <div className="flex flex-col items-start space-y-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Browser-Based</h3>
              <p className="text-gray-600">
                All text processing happens in your browser. Your text never leaves your device. Completely private and secure case conversion tool.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700">
              Need to change text case for titles, code, or documents? Our free case converter makes text transformation easy. Convert to uppercase for headings, lowercase for code, or title case for proper formatting. Perfect for writers, developers, and content creators. Try our text case converter today!
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link to="/online-notepad" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Online Notepad
              </Link>
              <Link to="/text-find-replace" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                → Find and Replace
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How to Use Case Converter Section */}
      <Card>
        <CardHeader>
          <h3 className="text-xl font-bold text-gray-900">How to Convert Text Case Online</h3>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4 text-gray-700">
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">1</span>
              <div>
                <strong>Enter Your Text:</strong> Paste or type your text in the input box on the left. The case converter accepts text of any length for transformation.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">2</span>
              <div>
                <strong>Select Case Style:</strong> Click the button for your desired case: Sentence case, lowercase, UPPERCASE, Capitalized Case, aLtErNaTiNg cAsE, Title Case, or InVeRsE CaSe.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">3</span>
              <div>
                <strong>View Result:</strong> Your converted text appears instantly in the output box on the right. The text case converter processes your text immediately.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-semibold">4</span>
              <div>
                <strong>Copy or Clear:</strong> Copy the converted text for use in your documents. Click Clear to reset and convert more text with different case styles.
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is the case converter free?</h3>
                {openFaq === 0 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 0 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our text case converter is completely free. Convert unlimited text between all 8 case styles without registration or fees.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">What is Title Case?</h3>
                {openFaq === 1 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 1 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Title Case capitalizes the first letter of each major word while keeping articles and prepositions lowercase (except at the start). Perfect for book titles, headlines, and headings.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Can I convert large amounts of text?</h3>
                {openFaq === 2 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 2 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Yes! Our case converter handles text of any length. Convert entire documents, paragraphs, or single words instantly with no text limits.</p>
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">Is my text data secure?</h3>
                {openFaq === 3 ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {openFaq === 3 && (
                <div className="p-4 bg-white">
                  <p className="text-gray-700">Absolutely! All text conversion happens in your browser. Your text is never uploaded to any server. Complete privacy guaranteed for all case conversions.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default CaseConverterPage;
